// The hot/cold indicator (ADR-0048): whether a worker for the chosen alias is
// up, busy, starting or asleep, polled from GET /availability, and what that
// means for the wait the user is about to sign up for. A sleeping backend is
// said out loud so a 4-minute first clip is expected, not a surprise.
import { useEffect, useState } from "react";
import { getAvailability, type AliasAvailability, type CatalogTarget } from "../api/client";
import { formatWait } from "./catalog";

export type Tone = "up" | "wait" | "down";

export interface AvailabilityView {
  tone: Tone;
  title: string;
  detail: string;
}

/** What one alias's state means for a clip of `target` (undefined: no estimate). */
export function availabilityView(state: AliasAvailability["state"], target?: CatalogTarget): AvailabilityView | undefined {
  const warm = target ? `about ${formatWait(target.estimate.warm_wait_s)}` : "the clip's own run time";
  const cold = target ? `about ${formatWait(target.estimate.cold_wait_s)}` : "a few minutes";
  switch (state) {
    case "warm":
      return { tone: "up", title: "Worker ready", detail: `Your clip starts right away and takes ${warm}.` };
    case "busy":
      return { tone: "up", title: "Worker busy", detail: `Your clip runs after the one in progress, then takes ${warm}.` };
    case "starting":
      return { tone: "wait", title: "Worker waking up", detail: "It is loading the model now; your clip runs as soon as it is ready." };
    case "cold":
      return {
        tone: "down",
        title: "Worker asleep",
        detail: `The first clip wakes one up: ${cold} in total. Clips right after it are much faster.`,
      };
    default:
      return undefined;
  }
}

/** The alias's availability, polled every `everyMs` while the view is open. */
export function useAvailability(alias: string, everyMs = 10_000): AliasAvailability | undefined {
  const [all, setAll] = useState<AliasAvailability[]>([]);
  useEffect(() => {
    let live = true;
    const load = () =>
      getAvailability()
        .then((r) => live && setAll(r.aliases))
        .catch(() => undefined);
    load();
    const timer = window.setInterval(load, everyMs);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [everyMs]);
  return all.find((a) => a.alias === alias);
}
